import { ASSET_V, FLAVORS, PACKS, byId, flavorById, money, productUrl } from 'v2/catalog.js';
import { $, $$, icons, canSrc, reducedMotion, initChrome, addToCart, openCart, shipMessage, toast } from 'v2/ui.js';
import { mountPack, mountStack } from 'v2/three-stage.js';
import { canSpin } from 'v2/can-spin.js';
import { asset } from 'v2/asset.js';

const pad = (n) => String(n).padStart(2, '0');
const desktop = matchMedia('(min-width: 1000px)');

/* ---------- Lineup shelf ---------- */
$('[data-shelf]').innerHTML = FLAVORS.map((f, i) => `
  <li class="shelf__item f-${f.id}" data-reveal style="--d:${i * 60}ms">
    <a class="shelf__link" href="${productUrl(f.id)}">
      <img class="shelf__can" src="${canSrc(f.id, 'md')}" srcset="${canSrc(f.id, 'sm')} 186w, ${canSrc(f.id, 'md')} 310w, ${canSrc(f.id)} 516w" sizes="(max-width: 999px) 80px, 9vw" alt="" width="310" height="600" loading="lazy" decoding="async">
      <span class="shelf__name">${f.name}</span>
    </a>
  </li>`).join('');
// On phones the shelf scrolls sideways, and cans past its edge never cross the viewport, so the per-can reveal left
// them invisible and nudged down (which also made the shelf scroll vertically under a swipe). Reveal the row together.
if ('IntersectionObserver' in window) {
  const shelfIO = new IntersectionObserver(([e]) => {
    if (!e.isIntersecting) return;
    $$('[data-reveal]', e.target).forEach((li) => li.classList.add('is-in'));
    shelfIO.disconnect();
  }, { threshold: 0.14 });
  shelfIO.observe($('[data-shelf]'));
}
// Each can turns while the pointer is on it (pre-rendered from the 3D can: js/can-spin.js). A can without a 3D model
// (catalog model: false, Cranberry Cheer) has no turn, so it stays put.
$$('.shelf__link').forEach((a, i) => { if (FLAVORS[i].model !== false) canSpin($('.shelf__can', a), FLAVORS[i].id, { zone: a }); });

/* ---------- Flavor rooms ---------- */
const roomsEl = $('[data-rooms]');
roomsEl.innerHTML = FLAVORS.map((f, i) => `
  <section class="room f-${f.id}" id="room-${f.id}" data-room="${i}" aria-labelledby="mast-${f.id}">
    <div class="room__head">
      <p class="kicker room__index">${f.icon ? `<img src="${asset(f.icon === true ? `brand/icon-${f.id}.svg` : f.icon)}" alt="">` : ''}No. ${pad(i + 1)} / ${pad(FLAVORS.length)}</p>
      <h2 class="room__mast" id="mast-${f.id}">${f.name}</h2>
    </div>
    <div class="room__stage">
      <div class="room__dome"><div class="room__dome-inner">
        <img src="${asset(`fruit/${f.id}-1600.webp`)}?v=${ASSET_V}" srcset="${asset(`fruit/${f.id}-900.webp`)}?v=${ASSET_V} 900w, ${asset(`fruit/${f.id}-1600.webp`)}?v=${ASSET_V} 1600w, ${asset(`fruit/${f.id}-2400.webp`)}?v=${ASSET_V} 2400w" sizes="(max-width: 999px) 100vw, 64vw" alt="" width="1600" height="893" loading="lazy" decoding="async">
      </div></div>
      <a class="room__can-link" href="${productUrl(f.id)}" tabindex="-1" aria-hidden="true"><img class="room__can" src="${canSrc(f.id)}" srcset="${canSrc(f.id, 'md')} 310w, ${canSrc(f.id)} 516w" sizes="(max-width: 999px) 32vw, 16vw" alt="" width="516" height="1000" loading="lazy" decoding="async"></a>
    </div>
    <div class="room__side">
      <span class="callout" aria-hidden="true"><b>10G</b><small>Protein</small></span>
      <span class="callout" aria-hidden="true"><b>ZERO</b><small>Sugar</small></span>
    </div>
    <div class="room__panel">
      <p class="room__line">${f.line}</p>
      <p class="room__desc">${f.desc}</p>
      <div class="room__facts"><span class="chip">10g protein</span><span class="chip">0g sugar</span><span class="chip">Caffeine free</span></div>
      <div class="room__buy">
        <p class="price">${money(f.price)}<small>12 cans · ${money(f.price / 12)} per can</small></p>
        <button class="btn btn--flavor" type="button" data-add="${f.id}">Add to cart<span class="visually-hidden">: a case of ${f.name}</span></button>
      </div>
      <a class="textlink room__more" href="${productUrl(f.id)}">Flavor details<span class="visually-hidden">: ${f.name}</span> <span aria-hidden="true">→</span></a>
    </div>
  </section>`).join('');

// Rail
const rail = $('[data-rail]');
rail.innerHTML = FLAVORS.map((f) => `<a class="f-${f.id}" href="#room-${f.id}" aria-label="${f.name}"><i></i><span>${f.name}</span></a>`).join('');

// Masthead fits the full width on desktop, capped by height so short names don't grow huge.
function fitMasts() {
  $$('.room__mast').forEach((el) => {
    if (!desktop.matches) { el.style.fontSize = ''; return; }
    const room = el.closest('.room');
    const avail = room.clientWidth - 2 * parseFloat(getComputedStyle(room).paddingLeft);
    el.style.fontSize = '100px';
    const w = el.scrollWidth;
    const cap = Math.min(innerHeight * 0.25, 260);
    el.style.fontSize = `${Math.min(cap, (100 * avail) / w)}px`;
  });
}
document.fonts?.ready.then(fitMasts);
addEventListener('resize', fitMasts);
fitMasts();

// Stacked rooms only when the layout fits a viewport.
roomsEl.classList.add('is-stacked');

// Active room: the last room whose top has passed the middle of the viewport.
const rooms = $$('[data-room]');
let active = -1;
function playOnly(idx) {
  rooms.forEach((r, i) => {
    const v = $('video', r);
    if (!v) return;
    if (i === idx && !reducedMotion) {
      if (!v.src) v.src = v.dataset.src;
      v.play().catch(() => {});
    } else v.pause();
  });
}
function updateRooms() {
  const mid = innerHeight * 0.55;
  const box = roomsEl.getBoundingClientRect();
  const inRooms = box.top < mid && box.bottom > innerHeight * 0.45;
  let idx = -1;
  rooms.forEach((r, i) => { if (r.getBoundingClientRect().top <= mid) idx = i; });
  if (!inRooms) idx = -1;
  rail.classList.toggle('is-on', inRooms);
  if (idx !== active) {
    active = idx;
    if (idx >= 0) rooms[idx].classList.add('is-in');
    $$('a', rail).forEach((a, i) => a.classList.toggle('is-active', i === idx));
    playOnly(idx);
  }
  // Preload the next room's loop just before it arrives.
  const next = rooms[Math.max(idx, 0) + (idx >= 0 ? 1 : 0)];
  const nv = next && $('video', next);
  if (nv && !nv.src && next.getBoundingClientRect().top < innerHeight * 1.6) { nv.src = nv.dataset.src; nv.preload = 'auto'; }
}

// Room transition (desktop, stacked): each room rises over the one before with a dome-shaped top edge in its own
// color that flattens as it fills the screen. --wipe runs from 1 (just entering) to 0 (in place); see site.css.
const stackQuery = matchMedia('(min-width: 1000px) and (min-height: 700px)');
function updateWipes() {
  if (reducedMotion || !stackQuery.matches) return;
  const h = innerHeight;
  rooms.forEach((r) => r.style.setProperty('--wipe', Math.min(1, Math.max(0, r.getBoundingClientRect().top / h)).toFixed(3)));
}

/* ---------- Hero dome: opens from the pack dome into a full-bleed film band ---------- */
const hero = $('[data-hero]');
const dome = $('[data-dome]');
let domeStart = 0;
const measureDome = () => { domeStart = dome.getBoundingClientRect().top + scrollY; };
function updateDome() {
  const p = Math.min(1, Math.max(0, scrollY / Math.max(1, domeStart - 90)));
  dome.style.setProperty('--p', reducedMotion ? 1 : p.toFixed(3));
}
// The hero film (2026-10-12, Matthew wanted the old tab-pull clip gone): a photoreal supercut, per flavor the can turning
// on a plinth, a wet-label close-up and its flavor notes, then the seven-can lineup; no hands, no sound
// (~/PRODA/creative/hero-film-2026-10-12, tools/cut.py there). Phones get the 4:5 cut. The poster is a CSS background
// on the dome (per breakpoint), so there is no wrong-poster flash; reduced motion keeps the poster. It starts after load.
const heroVideo = $('video', dome);
function startHero() {
  heroVideo.src = asset(desktop.matches ? 'film/hero-supercut-1080.mp4' : 'film/hero-supercut-vertical.mp4');
  heroVideo.preload = 'auto';
  if (dome.getBoundingClientRect().bottom > 0 && dome.getBoundingClientRect().top < innerHeight) heroVideo.play().catch(() => {});
}
if (!reducedMotion) {
  if (document.readyState === 'complete') setTimeout(startHero, 200);
  else addEventListener('load', () => setTimeout(startHero, 200), { once: true });
}
new IntersectionObserver(([e]) => {
  if (reducedMotion || !heroVideo.src) return;
  if (e.isIntersecting) heroVideo.play().catch(() => {}); else heroVideo.pause();
}).observe(dome);

/* ---------- Packs ---------- */
$('[data-packs]').innerHTML = PACKS.map((p, i) => `
  <article class="pack" data-reveal style="--d:${i * 90}ms">
    <a class="pack__stage" href="${productUrl(p.id)}" tabindex="-1" aria-hidden="true">
      <div class="pack__panels">${p.flavors.map((f) => `<span class="f-${f}"></span>`).join('')}</div>
      <div class="pack__cans">${p.flavors.map((f) => `<img src="${canSrc(f, 'md')}" alt="" width="310" height="600" loading="lazy">`).join('')}</div>
    </a>
    <div class="pack__body">
      <h3 class="pack__title">${p.name}</h3>
      <p class="pack__list">${p.flavors.map((f) => flavorById(f).name).join(', ')}. 12 cans, 3 of each.</p>
      <div class="pack__buy">
        <p class="price">${money(p.price)}<small>${money(p.price / 12)} per can</small></p>
        <button class="btn btn--orange" type="button" data-add="${p.id}">Add to cart<span class="visually-hidden">: the ${p.full}</span></button>
      </div>
      <a class="textlink" href="${productUrl(p.id)}">What's inside<span class="visually-hidden"> the ${p.name} pack</span> <span aria-hidden="true">→</span></a>
    </div>
  </article>`).join('');

// Each pack's real 3D tray takes over its stage once near the viewport (js/three-stage.js): the cans drop in.
$$('[data-packs] .pack').forEach((card, i) => mountPack($('.pack__stage', card), { tray: PACKS[i].id, columns: PACKS[i].flavors }));

/* ---------- Box builder ---------- */
const MAX_CASES = 6;
const picked = []; // ordered list of product ids, one entry per case
const picksEl = $('[data-picks]');
picksEl.innerHTML = [...FLAVORS, ...PACKS].map((p) => {
  const isPack = PACKS.includes(p);
  const cls = isPack ? 'f-pack' : `f-${p.id}`;
  return `<button type="button" class="pick ${cls}" data-pick="${p.id}">
    <img src="${canSrc(isPack ? p.flavors[0] : p.id, 'sm')}" alt="" width="186" height="360" loading="lazy">
    <span class="pick__name"><span class="visually-hidden">Add a case of </span>${p.name}${isPack ? ' Pack' : ''}</span>
    <span class="pick__price">${money(p.price)} / 12</span>
    ${p.tag ? `<span class="pick__tag">${p.tag}</span>` : ''}
    <span class="pick__count" data-count></span>
  </button>`;
}).join('');

// Flavor picks turn while hovered and spin when a case goes in; the pack picks show a single can, so they stay put, and
// so does a can without a 3D model (Cranberry Cheer).
const pickSpins = new Map(FLAVORS.filter((f) => f.model !== false).map((f) => {
  const b = $(`[data-pick="${f.id}"]`, picksEl);
  return [f.id, canSpin($('img', b), f.id, { zone: b })];
}));

const slotsEl = $('[data-box-slots]');

// The box: every case picked drops onto a stack of real 3D trays, three across and then a layer on top (Andrew,
// 2026-10-03: "instead of the video show boxes stacking"). Without 3D the still stays (js/three-stage.js).
const boxStage = $('[data-box-stage]');
const stack = mountStack(boxStage, { columnsFor: (id) => PACKS.find((p) => p.id === id)?.flavors || [id, id, id, id] });
// Room posters load a screen ahead of each room, so seven of them never compete with the hero.
const posterIO = new IntersectionObserver((entries) => entries.forEach((e) => {
  if (!e.isIntersecting) return;
  e.target.poster = e.target.dataset.poster;
  posterIO.unobserve(e.target);
}), { rootMargin: '100% 0px' });
$$('.room video[data-poster]').forEach((v) => posterIO.observe(v));
function tops(id) {
  const p = byId(id);
  if (PACKS.includes(p)) {
    return p.flavors.flatMap((f) => Array(3).fill(`<i style="--c:${flavorById(f).field}"></i>`)).join('');
  }
  return Array(12).fill(`<i style="--c:${p.field}"></i>`).join('');
}
function renderBox() {
  const counts = picked.reduce((m, id) => ((m[id] = (m[id] || 0) + 1), m), {});
  $$('[data-pick]', picksEl).forEach((b) => {
    const n = counts[b.dataset.pick] || 0;
    b.classList.toggle('is-picked', n > 0);
    $('[data-count]', b).textContent = n;
  });
  slotsEl.innerHTML = Array.from({ length: MAX_CASES }, (_, i) => picked[i]
    ? `<button type="button" class="slot is-filled" data-unpick="${i}" aria-label="Remove ${byId(picked[i]).name} case"><span class="slot__tops">${tops(picked[i])}</span></button>`
    : `<div class="slot">Case ${i + 1}</div>`).join('');
  const cases = picked.length;
  const total = picked.reduce((n, id) => n + byId(id).price, 0);
  $('[data-box-count]').textContent = `${cases} ${cases === 1 ? 'case' : 'cases'}`;
  $('[data-box-cans]').textContent = cases ? `${cases * 12} cans · ${money(total / (cases * 12))} per can` : 'Pick at least one case';
  $('[data-box-total]').textContent = money(total);
  const m = shipMessage(cases);
  $('[data-box-cap]').textContent = cases >= MAX_CASES ? 'Free shipping. Box full.' : m.free ? 'Free shipping. Keep stacking.' : 'Two cases ship free';
  boxStage.classList.toggle('is-empty', !cases);
  // A case without a 3D tray (Cranberry Cheer) counts, prices and ships like any other and fills its slot below, but
  // has no tray to drop into the 3D stack.
  stack.set(picked.filter((id) => byId(id).model !== false));
  const meter = $('[data-box-meter]');
  meter.classList.toggle('is-free', m.free);
  $('[data-meter-msg]', meter).innerHTML = m.html;
  $('.meter__fill', meter).style.width = `${m.pct}%`;
  $('[data-box-add]').disabled = !cases;
}
picksEl.addEventListener('click', (e) => {
  const b = e.target.closest('[data-pick]');
  if (!b) return;
  if (picked.length >= MAX_CASES) {
    toast(byId(b.dataset.pick), 'Six cases fills this box. Add more from the cart.');
    return;
  }
  picked.push(b.dataset.pick);
  pickSpins.get(b.dataset.pick)?.spin();
  renderBox();
});
slotsEl.addEventListener('click', (e) => {
  const s = e.target.closest('[data-unpick]');
  if (!s) return;
  picked.splice(Number(s.dataset.unpick), 1);
  renderBox();
});
$('[data-box-clear]').addEventListener('click', () => { picked.length = 0; renderBox(); });
$('[data-box-add]').addEventListener('click', () => {
  const counts = picked.reduce((m, id) => ((m[id] = (m[id] || 0) + 1), m), {});
  Object.entries(counts).forEach(([id, n]) => addToCart(id, n, { quiet: true }));
  picked.length = 0;
  renderBox();
  openCart();
});
renderBox();

/* ---------- Fruit reel: each flavor up close, linked to its page ---------- */
const reel = $('[data-reel]');
reel.innerHTML = FLAVORS.map((f) => `
  <div class="reel__item f-${f.id}">
    <a class="reel__btn" href="${productUrl(f.id)}">
      <img src="${asset(`fruit/${f.id}-1600.webp`)}?v=${ASSET_V}" srcset="${asset(`fruit/${f.id}-900.webp`)}?v=${ASSET_V} 900w, ${asset(`fruit/${f.id}-1600.webp`)}?v=${ASSET_V} 1600w" sizes="(max-width: 999px) 84vw, 64vw" alt="" width="1600" height="893" loading="lazy" decoding="async">
      <span class="reel__meta">
        <span><span class="reel__title">${f.name}</span><span class="reel__sub"><i></i>10g protein · Zero sugar</span></span>
        <span class="reel__play">${icons.next}</span>
      </span>
    </a>
  </div>`).join('');
const step = () => ($('.reel__item', reel)?.getBoundingClientRect().width || 600) + 20;
$('[data-reel-prev]').addEventListener('click', () => reel.scrollBy({ left: -step(), behavior: 'smooth' }));
$('[data-reel-next]').addEventListener('click', () => reel.scrollBy({ left: step(), behavior: 'smooth' }));
$$('[data-reel-prev], [data-reel-next]').forEach((b) => { b.innerHTML = b.matches('[data-reel-prev]') ? icons.prev : icons.next; });

/* ---------- Founders band: Matthew, Jeff and Joey are public; the five come with the code ---------- */
// The full five live in founders/section.html, which the server only serves with the code (middleware.js). The
// homepage no longer carries the code form (Andrew, 2026-10-12), so the gate lines below run only if it comes back.
const foundersBand = $('[data-home-founders]');
if (foundersBand) {
  const ask = new URLSearchParams(location.search).get('founders');
  const gate = $('[data-founders-gate]', foundersBand);
  if (gate && (ask === 'code' || ask === 'retry')) {
    gate.hidden = false;
    $('[data-founders-err]', foundersBand).hidden = ask !== 'retry';
    history.replaceState(null, '', `${location.pathname}${location.hash}`);
  }
  Promise.resolve({ status: 204 }) // Shopify: the founders band arrives with the announcement
    .then((r) => (r.status === 200 ? r.text() : null)) // 204 without the code
    .then((html) => {
      if (!html) return;
      foundersBand.innerHTML = html;
      foundersBand.classList.add('is-unlocked');
      if (location.hash === '#founders') foundersBand.scrollIntoView();
    })
    .catch(() => {});
}

$('[data-founders-local]')?.addEventListener('submit', (e) => { e.preventDefault(); const err = $('[data-founders-err]'); if (err) err.hidden = false; });

/* ---------- Nav state ---------- */
const navLinks = $$('.chrome__nav a[href^="/#"]');
const sections = navLinks.map((a) => $(a.getAttribute('href').slice(1))).filter(Boolean);
function updateNav() {
  const mid = innerHeight * 0.4;
  let current = null;
  sections.forEach((s) => { const r = s.getBoundingClientRect(); if (r.top <= mid && r.bottom > mid) current = s; });
  navLinks.forEach((a) => a.classList.toggle('is-active', Boolean(current) && a.getAttribute('href') === `/#${current.id}`));
}

/* ---------- Scroll loop ---------- */
let ticking = false;
function onScroll() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(() => { updateDome(); updateRooms(); updateWipes(); updateNav(); ticking = false; });
}
addEventListener('scroll', onScroll, { passive: true });
addEventListener('resize', () => { measureDome(); onScroll(); });
measureDome();
initChrome();
onScroll();
if (location.hash) requestAnimationFrame(() => $(location.hash)?.scrollIntoView());
